import { useCallback, useEffect, useRef, useState } from "react";

type ResizeObserverLike = {
  observe(target: Element): void;
  disconnect(): void;
};

type ResizeObserverConstructor = new (
  callback: ResizeObserverCallback
) => ResizeObserverLike;

export type MeasureOptions = {
  debounce?: number | { resize?: number };
  polyfill?: ResizeObserverConstructor;
};

export type MeasuredBounds = {
  x: number;
  y: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
};

const EMPTY_BOUNDS: MeasuredBounds = {
  x: 0,
  y: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: 0,
  height: 0,
};

function toBounds(rect: DOMRect | DOMRectReadOnly): MeasuredBounds {
  return {
    x: rect.x,
    y: rect.y,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function boundsEqual(a: MeasuredBounds, b: MeasuredBounds): boolean {
  return (
    a.x === b.x &&
    a.y === b.y &&
    a.top === b.top &&
    a.right === b.right &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

export default function useElementMeasure({
  debounce = 0,
  polyfill,
}: MeasureOptions = {}) {
  const [bounds, setBounds] = useState<MeasuredBounds>(EMPTY_BOUNDS);
  const elementRef = useRef<HTMLElement | SVGElement | null>(null);
  const observerRef = useRef<ResizeObserverLike | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const updateBounds = useCallback(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    const nextBounds = toBounds(element.getBoundingClientRect());
    setBounds((currentBounds) =>
      boundsEqual(currentBounds, nextBounds) ? currentBounds : nextBounds
    );
  }, []);

  const scheduleMeasure = useCallback(() => {
    const wait =
      typeof debounce === "number" ? debounce : (debounce.resize ?? 0);

    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    if (wait > 0) {
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        updateBounds();
      }, wait);
      return;
    }

    updateBounds();
  }, [debounce, updateBounds]);

  const ref = useCallback(
    (element: HTMLElement | SVGElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      elementRef.current = element;

      if (!element) {
        return;
      }

      updateBounds();

      const ResizeObserverImpl =
        polyfill ??
        (typeof window !== "undefined" ? window.ResizeObserver : undefined);

      if (!ResizeObserverImpl) {
        return;
      }

      const observer = new ResizeObserverImpl(() => {
        scheduleMeasure();
      });

      observer.observe(element);
      observerRef.current = observer;
    },
    [polyfill, scheduleMeasure, updateBounds]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      scheduleMeasure();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [scheduleMeasure]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
      observerRef.current?.disconnect();
    };
  }, []);

  return [ref, bounds] as const;
}
